// A suggested Uptick work order for a waiting result (uptick_plan.md §12, push rule 2, revised 2026-11-03): found by
// a live look-up of its battery set's lines — the set from the result's battery ID (linked to an Uptick asset) or an
// A-number test name — open lines plus lines already ticked on site. Only a suggestion: a person checks the site and
// battery set and sends it with one click; the cloud keeps a matching tick and asks before replacing a different
// one. Shared by the waiting list on the Integrations page and the test's Uptick card.

import { Link } from "react-router-dom";
import type { UptickSuggestion, UptickSuggestedJob } from "../lib/api.ts";

function jobLine(j: UptickSuggestedJob) {
  return [
    j.task_ref ? `Work order ${j.task_ref}` : `Service task #${j.servicetask_id}`,
    j.due ? `due ${new Date(`${j.due}T00:00:00`).toLocaleDateString()}` : null,
    [j.site, j.address].filter(Boolean).join(" — ") || null,
    [j.asset_ref, j.location].filter(Boolean).join(" ") || null,
    j.ticked ? `already ticked ${j.ticked}${j.performed_date ? ` on ${new Date(j.performed_date).toLocaleDateString()}` : ""}` : null,
  ].filter(Boolean).join(" · ");
}

export function UptickSuggestionView(props: {
  suggestion: UptickSuggestion;
  sending: boolean;
  onSend: (servicetaskId: number) => void;
}) {
  const s = props.suggestion;
  if (s.kind === "unlinked") {
    return (
      <p className="muted" style={{ fontSize: "0.9em", margin: "0.25em 0" }}>
        Battery <Link to={`/batteries/${s.battery_id}`}>{s.battery_id}</Link> is not linked to an Uptick battery set
        yet — link it once on its page, or pick the work order on the test (the link is then learnt).
      </p>
    );
  }
  if (s.kind === "collision") {
    return (
      <p className="err" style={{ fontSize: "0.9em", margin: "0.25em 0" }}>
        Battery ID <Link to={`/batteries/${s.battery_id}`}>{s.battery_id}</Link> was created again by this test
        although it already exists — two battery sets may carry the same ID. Check the label before sending.
      </p>
    );
  }
  if (s.kind === "none") {
    return (
      <p className="muted" style={{ fontSize: "0.9em", margin: "0.25em 0" }}>
        No open or freshly ticked battery work order for {s.ref} in Uptick — check the number, or pick the work
        order on the test.
      </p>
    );
  }
  const options = s.kind === "match" ? [s] : s.options;
  return (
    <div style={{ margin: "0.35em 0" }}>
      <p style={{ fontSize: "0.9em", margin: "0.25em 0" }}>
        {s.kind === "match" ? "Suggested for this battery set — check the site and battery set:" :
          `${options[0]?.asset_ref ?? "This battery set"} has ${options.length} open work orders — pick one:`}
      </p>
      {options.map((o) => (
        <p key={o.servicetask_id} style={{ fontSize: "0.9em", margin: "0.25em 0" }}>
          <span>{jobLine(o)}</span>{" "}
          <button type="button" disabled={props.sending} onClick={() => props.onSend(o.servicetask_id)}>
            {props.sending ? "Sending…" : "Send to this work order"}
          </button>
        </p>
      ))}
      {s.kind === "match" && s.warnings.length > 0 && (
        <p className="err" style={{ fontSize: "0.85em", margin: "0.25em 0" }}>
          Tester and Uptick disagree — {s.warnings.join("; ")}. Make sure this is the right battery set.
        </p>
      )}
    </div>
  );
}
